/* ================================================================
 * Copyright © 2025 Tobeunicorn Co., Ltd. All rights reserved.
 *
 * 프로젝트명 : ArmyLink V1.0
 * 설명 : 메인페이지 컴포넌트
 * 버전 : 1.0
 * 파일명 : HomePage.tsx
 * 작성자 : 강동형
 * 작성일 : 2025-03-01
 * ================================================================ 
 * 수정 : 2025-07-14, 강동형 : 저작권 추가, 클릭하여 상세보기로 갈 수 있도록 로직 추가
 * ================================================================
 */

import { Calendar, dateFnsLocalizer} from 'react-big-calendar'
import {format, parse, startOfWeek, getDay, isWithinInterval, startOfDay, endOfDay} from 'date-fns'
import { ko } from 'date-fns/locale';
import React, { useEffect } from 'react';
import { useAdRecruitStore } from 'src/stores/AdRecruitStore';
import { useModalStore } from 'src/stores/ModalStore';
import { RecruitScheduleData } from './useLoadRecruitSchedule';
import { ModalContent_RecruitCalendar_Mini, ModalTitle_RecruitCalendar_Mini } from './ModalRecruitCalendar';
import { ModalContent_ModalRecruitCalendarDetail, ModalTitle_ModalRecruitCalendarDetail } from './ModalRecruitCalendarDetail';

const locales = {
    'ko': ko,
  };

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
})

const RCalendar = Calendar as unknown as React.FC<any>;

export type CalendarEvent = {
  title: string;
  start: Date;
  end: Date;
  announce_id: number;
  course_type: string;
  course_name: string;
};

const AdRecruitCalendar = () => {
  const setField = useAdRecruitStore((state)=>state.setField)
  const currnetdate = useAdRecruitStore((state)=>state.currnetdate)
  const type = useAdRecruitStore((state)=>state.type)
  const status = useAdRecruitStore((state)=>state.status)
  const recruitScheduleData = useAdRecruitStore((state)=>state.recruitScheduleData)
  const modalsetField = useModalStore((state)=>state.setField)

  const filteredEvents = recruitScheduleData.filter((event) => {
    const typeMatch = type === '' || event.recruitment_type === type;
    const statusMatch = status === '' || event.calculated_status === status.replace(/\s/g, '');

    return typeMatch && statusMatch;
  });

  const convertToEvents = (data: RecruitScheduleData[]): CalendarEvent[] => {
    return data.map(item => {
      // 먼저 item.etc를 사용해 기존의 이벤트를 변환
      const events = item.etc.map(schedule => {
        return {
          title: item.title,
          start: new Date(schedule.start_date),
          end: new Date(schedule.end_date),
          announce_id: item.announce_id,
          course_type: schedule.course_name === '입영일' || schedule.course_name === '임관일' ? schedule.course_name : '추가과정',
          course_name: schedule.course_name
        };
      });

      // application_sdate와 application_edate를 추가한 이벤트
      const applicationEvent = {
        title: item.title,
        start: new Date(item.application_sdate),
        end: new Date(item.application_edate),
        announce_id: item.announce_id,
        course_type: '접수', // Assuming 'recruitment_type' is relevant
        course_name : '접수'
      };
      return [...events, applicationEvent];
    }).flat();
  };

  const convertedEvents = convertToEvents(filteredEvents)

useEffect(() => {
  setField('pageSize',100)
  return () => {
    setField('pageSize',8)
  };
}, []);

  const onDrillDown = (date: Date, event: React.SyntheticEvent)=>{
    // 여기서 drilldown 기본 동작을 막는다
    event?.preventDefault?.();
    event?.stopPropagation?.();
    //날짜에 해당하는 이벤트
    const eventArray = convertedEvents.filter(event => {
      return isWithinInterval(date, {
        start: startOfDay(new Date(event.start)),
        end: endOfDay(new Date(event.end)),
      });
    });
    modalsetField('type','CENTER')
    modalsetField('width','560px')
    modalsetField('padding','20px')
    modalsetField('title',<ModalTitle_RecruitCalendar_Mini/>)
    modalsetField('content',<ModalContent_RecruitCalendar_Mini items={eventArray}/>)
    // setField('currnetdate',date)
    modalsetField('open',true)
  }
  
  const handleSelectEvent = (events:CalendarEvent)=>{
    modalsetField('type','CENTER')
    modalsetField('width','1120px')
    modalsetField('title',<ModalTitle_ModalRecruitCalendarDetail item={events}/>)
    modalsetField('content',<ModalContent_ModalRecruitCalendarDetail item={events}/>)
    modalsetField('open',true)
  }


  const nowBgColorMap: Record<string, string> = {
    '접수': '#3E6F7D',
    '추가과정': '#439787',
    '입영일': '#6DD7BB',
    '임관일': '#A4E6D5',
  };

  const nowColorMap: Record<string, string> = {
    '접수': 'white',
    '추가과정': 'white',
    '입영일': '#0B4D3C',
    '임관일': '#0B4D3C',
  };

    return(
            <RCalendar
            localizer={localizer}
            events={convertedEvents}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '600px' }}
            date={currnetdate}
            toolbar = {false} // 상단 날짜선택 옵션 툴바
            drilldownView="day" // 날짜 클릭시 실행하는 이벤트트
            onDrillDown={onDrillDown}
            onSelectEvent={handleSelectEvent}
            eventPropGetter={(event : any) => {
              const backgroundColor = nowBgColorMap[event.course_type] || 'lightgray';
              const color = nowColorMap[event.course_type] ||'black'

              return {
                style: {
                  backgroundColor: backgroundColor,
                  color: color,
                  borderRadius: '5px',
                  border: 'none',
                  padding: '3px',
                  fontSize:'14px',
                  textAlign:'center'
                },
              };
            }}
            />
        )
}

export default AdRecruitCalendar